import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useFormik } from "formik";

import { useLayout } from "@/../_metronic/layout/core";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import { useCurrentAccount } from "@/hooks/useCurrentAccount";

import {
	getHospitalizationDashboard,
	HospitalizationDashboardData,
} from "@/services/sintegrada/attendance/getHospitalizationDashboard";
import { hospitalizationFilterInitialValues } from "@/utils/initialValues/hospitalizationFilter";
import { hospitalizationFilterValidationSchema } from "@/utils/Schemas/hospitalizationFilterValidationSchema";

import { HospitalizationDashboardFilters } from "./HospitalizationDashboardFilters";
import { BarChart } from "@/components/Charts/BarChart";
import { DonutChart } from "@/components/Charts/DonutChart";
import { HospitalizationDashboardCards } from "./HospitalizationDashboardCards";
import { PermanenceOccupationMetrics } from "./PermanenceOccupationMetrics";
import { LoadingScreen } from "@/components/LoadingScreen";
import { PrintComponent } from "@/components/PrintComponent";
import { defaultHospitalWardSelectOption } from "@/components/sintegrada/Selects/HospitalWardSelect";

dayjs.extend(utc);
dayjs.extend(timezone);

export function HospitalizationDashboard() {
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const { setTitle } = useLayout();

	const [dashboard, setDashboard] = useState<HospitalizationDashboardData>();
	const [isLoading, setIsLoading] = useState(false);
	const printPageRef = useRef<HTMLDivElement | null>(null);
	const isInitialDepartmentLoad = useRef(true);

	const printPage = useReactToPrint({
		content: () => printPageRef.current,
	});

	const dashboardData = dashboard || {
		aggregatedByGender: [],
		aggregatedByHealthUnitTransfer: [],
		aggregatedByTypeAndTimeClassification: [],
		aggregatedObservationsByPeriodAndType: [],
		averageHospitalizationsTimeInSecondsByType: [],
		totalBriefObservations: 0,
		totalHospitalizations: 0,
		totalLongObservations: 0,
		totalStabilizations: 0,
		totalPendingAdmittedObservations: 0,
		totalPendingAdmittedHospitalizations: 0,
		totalPendingAdmittedStabilizations: 0,
		totalPendingNotAdmittedObservations: 0,
		totalPendingNotAdmittedHospitalizations: 0,
		totalPendingNotAdmittedStabilizations: 0,
		totalDeaths: 0,
		totalHospitalizationsAdmitted: 0,
		totalHospitalizationsAdmittedByGender: [],
		deathsReports: {
			totalDeaths: 0,
			totalDeathsWithin24h: 0,
			totalDeathsOver24h: 0,
		},
		occupationReports: {
			totalOccupation: 0,
			totalOccupationByGender: [],
		},
		transfersReports: {
			totalTransfersByUnit: [],
			totalTransfers: 0,
		},
		permanenceAndOccupationMetrics: {
			permanenceMetrics: {
				tempoMedioPermanencia: 0,
				quantidadeMenor24h: 0,
				quantidadeEntre24h48h: 0,
				quantidadeEntre48h72h: 0,
				quantidadeMaior72h: 0,
				totalPermanencias: 0,
			},
			occupationRate: {
				totalOcupacoes: 0,
				totalLeitos: 0,
				diasPeriodo: 0,
				taxaOcupacao: 0,
			},
		},
	};

	const formik = useFormik({
		initialValues: hospitalizationFilterInitialValues,
		validationSchema: hospitalizationFilterValidationSchema,
		async onSubmit(values) {
			setIsLoading(true);

			const params = {
				uf,
				ibge_code: ibgeCode,
				cnes,
				start_date: dayjs(values.startDate).format("YYYY-MM-DD HH:mm:ss"),
				end_date: dayjs(values.endDate).format("YYYY-MM-DD HH:mm:ss"),
				min_age: Number(values.minAge) || null,
				max_age: Number(values.maxAge) || null,
				department_id: values.department.value?.id,
				hospital_ward_id: values.hospitalWard.value?.id,
			};

			const data = await getHospitalizationDashboard(params);

			setDashboard(data);

			setIsLoading(false);
		},
	});

	const { values } = formik;

	const { startDate, endDate, minAge, maxAge } = values;

	const startDateFormatted = dayjs(startDate).format("DD/MM/YYYY");
	const endDateFormatted = dayjs(endDate).format("DD/MM/YYYY");

	function handleDepartmentChange() {
		const selectedDepartment = values.department?.value;

		if (selectedDepartment?.id && isInitialDepartmentLoad.current) {
			isInitialDepartmentLoad.current = false;
			formik.handleSubmit();

			return;
		}

		formik.setFieldValue("hospitalWard", defaultHospitalWardSelectOption);
	}

	useEffect(() => {
		setTitle("Monitoramento - Pacientes hospitalizados");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		handleDepartmentChange();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.department]);

	return (
		<div className="mx-3">
			<LoadingScreen loading={isLoading} />
			<Row className="p-2">
				<Card className="h-100 shadow-sm">
					<Card.Body>
						<HospitalizationDashboardFilters formik={formik} handlePrint={printPage} />
					</Card.Body>
				</Card>
			</Row>

			<PrintComponent
				printComponentRef={printPageRef}
				headerContainer={
					<div>
						<p className="text-center fs-3">Dashboard de Hospitalização</p>
						{startDate && endDate && (
							<div>
								<p className="text-center">
									<span>{`${startDateFormatted} A ${endDateFormatted}`}</span>
								</p>
							</div>
						)}
						{minAge && maxAge && (
							<p>
								<strong>Faixa etária:</strong>
								<span>{`${minAge} anos á ${maxAge} anos`}</span>
							</p>
						)}

						<p>
							<strong>Setor:</strong>
							<span>{values.department.value?.name}</span>
						</p>
					</div>
				}
			>
				<Row className="ms-2 p-2">
					<HospitalizationDashboardCards
						totalObservations={dashboardData.totalHospitalizationsAdmitted}
						totalBriefObservations={dashboardData.occupationReports.totalOccupation}
						totalHospitalizations={dashboardData.transfersReports.totalTransfers}
						totalDeaths={dashboardData.deathsReports.totalDeaths}
						deathsLessThan24h={dashboardData.deathsReports.totalDeathsWithin24h}
						deathsMoreThan24h={dashboardData.deathsReports.totalDeathsOver24h}
					/>
				</Row>

				{/* Métricas de Permanência e Ocupação */}
				<Row className="ms-2 p-2">
					<PermanenceOccupationMetrics
						permanenceAndOccupationMetrics={
							dashboardData.permanenceAndOccupationMetrics
						}
					/>
				</Row>

				<Row className="p-2">
					<Col sm={12} lg={6} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Admitidos por gênero</Card.Title>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<DonutChart
									colors={["#04e69a", "#f1416c", "#ffd52f", "#b4b5c2"]}
									labels={dashboardData.totalHospitalizationsAdmittedByGender.map(
										(group) =>
											group.key.toUpperCase() === "F"
												? "Feminino"
												: group.key.toUpperCase() === "M"
												? "Masculino"
												: "Outros"
									)}
									series={dashboardData.totalHospitalizationsAdmittedByGender.map(
										(group) => group.total
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
					<Col sm={12} lg={6} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Ocupação por gênero</Card.Title>
							</Card.Header>
							<Card.Body className="d-flex justify-content-center align-items-center">
								<DonutChart
									colors={["#008FFB", "#7239ea", "#ffd52f", "#b4b5c2"]}
									labels={dashboardData.occupationReports.totalOccupationByGender.map(
										(group) =>
											group.key.toUpperCase() === "F"
												? "Feminino"
												: group.key.toUpperCase() === "M"
												? "Masculino"
												: "Outros"
									)}
									series={dashboardData.occupationReports.totalOccupationByGender.map(
										(group) => group.total
									)}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				<Row className="p-2 page-break-before">
					<Col sm={12} lg={12} className="mb-2">
						<Card className="h-100 shadow-sm">
							<Card.Header className="d-flex fs-2 fw-bolder justify-content-start align-items-center">
								<Card.Title>Transferências de pacientes por unidade</Card.Title>
							</Card.Header>
							<Card.Body>
								<BarChart
									categories={dashboardData.transfersReports.totalTransfersByUnit.map(
										(group) => group.key
									)}
									series={[
										{
											name: "Qtde de transferências",
											data: dashboardData.transfersReports.totalTransfersByUnit.map(
												(group) => group.total
											),
										},
									]}
									colors={["#04e69a"]}
									categoryTextWidth={400}
									baseMultiplier={10}
								/>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</PrintComponent>
		</div>
	);
}
